import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, defer, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { EligibilityService } from '@core';
import {
  CapDeductibleAccumulation,
  CapDeductibleRequestInterface,
  CapDeductiblesResponse,
  MemberDetails,
} from '@interfaces';
import {
  AppCenterAnalytics,
  AppInsightsService,
  SharedService,
} from '@services';
import { LoaderService } from '@management';
import { AppConstants, CapDeductibleTypes } from '../../../app.constants';

@Component({
  selector: 'app-cap-deductible-moop',
  templateUrl: './cap-deductible-moop.component.html',
  styleUrls: ['./cap-deductible-moop.component.scss'],
  standalone: false,
})
export class CapDeductibleMoopComponent implements OnInit {
  deductible = false;
  cap = false;
  moop = false;
  displayedColumnsDeductible: string[] = [
    'Phase',
    'Frequency',
    'FamilyTotal',
    'FamilyAccum',
    'Member',
    'MemberAccum',
    'Single',
    'SingleAccum',
  ];
  displayedColumnsCap: string[] = [
    'Phase',
    'CapType',
    'Frequency',
    'FamilyAccum',
    'Member',
    'MemberAccum',
    'Single',
    'SingleAccum',
  ];
  displayedColumnsMoop: string[] = [
    // 'DrugCategory',
    // 'Type',
    'Phase',
    'CapType',
    'Frequency',
    'Family',
    'FamilyAccum',
    'Member',
    'MemberAccum',
    'Single',
    'SingleAccum',
  ];
  displayedColumnsFill: string[] = [
    'Phase',
    'CapType',
    'Frequency',
    'Family',
    'FamilyAccum',
    'Member',
    'MemberAccum',
    'Single',
    'SingleAccum',
  ];
  displayedColumnsIpp: string[] = [
    'Phase',
    'CapType',
    'Frequency',
    'Family',
    'FamilyAccum',
    'Member',
    'MemberAccum',
    'Single',
    'SingleAccum',
  ];
  displayedColumnsMpp: string[] = [
    'Phase',
    'CapType',
    'Frequency',
    'Family',
    'FamilyAccum',
    'Member',
    'MemberAccum',
    'Single',
    'SingleAccum',
  ];
  displayedColumnsOpp: string[] = [
    'Phase',
    'CapType',
    'Frequency',
    'Family',
    'FamilyAccum',
    'Member',
    'MemberAccum',
    'Single',
    'SingleAccum',
  ];

  dataSourceDeductible: CapDeductibleAccumulation[] = [];
  dataSourceCap: CapDeductibleAccumulation[] = [];
  dataSourceMoop: CapDeductibleAccumulation[] = [];
  dataSourceFill: CapDeductibleAccumulation[] = [];
  dataSourceIpp: CapDeductibleAccumulation[] = [];
  dataSourceMpp: CapDeductibleAccumulation[] = [];
  dataSourceOpp: CapDeductibleAccumulation[] = [];

  capDeductibleMoop$!: Observable<CapDeductiblesResponse>;

  public selectedMemberDetails$: Observable<Partial<MemberDetails>>;
  clientId = '';
  cardId = '';
  public displayCoverageType = AppConstants.DISPLAY_COVERAGE_TYPE;
  personCode = '';
  public relationShip = AppConstants.RELATIONSHIP_VALUES;
  public isExpanded = true;

  public navigateBackToMemberSearch(): void {
    this.router.navigateByUrl('/management/online-eligibility');
  }

  constructor(
    private router: Router,
    private readonly appInsightsService: AppInsightsService,
    private activatedRoute: ActivatedRoute,
    private eligibilityService: EligibilityService,
    private loader: LoaderService,
    private sharedService: SharedService
  ) {}

  ngOnInit(): void {
    this.appInsightsService.trackPageView(
      AppCenterAnalytics.CAP_DEDUCTIBLE_MOOP
    );
    this.getSelectedMemberDetails();
  }

  getCapDeductibleMoopData() {
    const reqBody: CapDeductibleRequestInterface = {
      clientId: this.clientId,
      cardId: this.cardId,
      personCode: this.personCode,
    };
    this.capDeductibleMoop$ = defer(() => {
      this.loader.showLoader();
      return this.eligibilityService.getCapDeductibleMoopDetails(reqBody).pipe(
        tap((res) => {
          const accumulation = res.capDeductibleAccumulation ?? [];

          const typeMap = {
            dataSourceDeductible: CapDeductibleTypes.STANDARD_DEDUCTIBLE,
            dataSourceMoop: CapDeductibleTypes.MAX_OUT_OF_POCKET,
            dataSourceFill: CapDeductibleTypes.FILL,
            dataSourceIpp: CapDeductibleTypes.INITIAL_PLAN_PAY,
            dataSourceMpp: CapDeductibleTypes.MAX_PAID_BY_PLAN,
            dataSourceOpp: CapDeductibleTypes.OUT_OF_POCKET_PHASE,
          };

            this.sortDeductiblesByPhase(typeMap, accumulation);
          this.loader.hideLoader();
        }),
        catchError((err) => {
          this.loader.hideLoader();
          return of({ groupName: '', capDeductibleAccumulation: [] });
        })
      );
    });
  }

  private sortDeductiblesByPhase(typeMap: { dataSourceDeductible: CapDeductibleTypes; dataSourceMoop: CapDeductibleTypes; dataSourceFill: CapDeductibleTypes; dataSourceIpp: CapDeductibleTypes; dataSourceMpp: CapDeductibleTypes; dataSourceOpp: CapDeductibleTypes; }, accumulation: CapDeductibleAccumulation[]): void {
    Object.entries(typeMap).forEach(([key, type]) => {
      this[key] = accumulation
        .filter(
          (item) => item.type?.toLowerCase().trim() ===
            type.toLowerCase().trim() &&
            (item.phase != null || item.alias != null)
        )
        .map((item) => {
          item.phase = item.phase ? item.phase : item.alias;
          return item;
        })
        .sort((a, b) => {
          const valA = (a.phase ?? '').toLowerCase();
          const valB = (b.phase ?? '').toLowerCase();
          return valA.localeCompare(valB, undefined, { numeric: true });
        });
    });
  }

  get isLevelD(): boolean {
    return this.sharedService.isLevelD();
  }

  getSelectedMemberDetails() {
    this.loader.showLoader();
    this.selectedMemberDetails$ = this.activatedRoute.queryParams.pipe(
      switchMap((res) => {
        return this.eligibilityService.getMemberDetailsById(res).pipe(
          map((res) => res[0]),
          tap((resp) => {
            // console.log('Res:', resp);
            this.appInsightsService.trackPageView(
              AppCenterAnalytics.CAP_DEDUCTIBLE_MOOP_API_SUCCESS,
              { Response: res }
            );
            this.clientId = resp.clientId;
            this.cardId = resp.cardId;
            this.personCode = resp.personCode;
            this.loader.hideLoader();
            this.getCapDeductibleMoopData();
          }),
          catchError((err) => {
            this.appInsightsService.trackPageView(
              AppCenterAnalytics.CAP_DEDUCTIBLE_MOOP_API_ERROR,
              { Response: err }
            );
            this.loader.hideLoader();
            return of([]);
          })
        );
      })
    );
  }

  public toggle() {
    this.isExpanded = !this.isExpanded;
  }
}
